/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * https://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
import CMacro from './CMacro'
export default CMacroAffectationPointLie

/**
 * Macro envoyant un point lié sur un autre point lié au même objet.
 * @constructor
 * @extends CMacro
 * @param {CListeObjets} listeProprietaire  La liste propriétaire.
 * @param {CImplementationProto} impProto  null ou la construction propriétaire.
 * @param {boolean} estElementFinal  true si l'objet est un élément final de construction.
 * @param {Color} couleur  La couleur d'éciture de l'éditeur (et du cadre éventuel).
 * @param {number} xNom  L'abscisse d'affichage de la macro
 * @param {number} yNom  L'ordonnée d'affichage de la macro
 * @param {number} decX  Décalage horizontal du nom
 * @param {number} decY  Décalage vertical du nom
 * @param {boolean} masque  true si l'éditeur est masqué
 * @param {CPt} pointLie  null ou pointe sur un point auquel l'affichage est lié.
 * @param {number} taillePolice  Indice de la taiile de police utilisée
 * @param {boolean} effacementFond  true si on efface le fond de l'en-tête.
 * @param {Color} couleurFond  La couleur de fond de l'en-tête.
 * @param {number} alignementHorizontal  0 pour alignement gauche, 1 pour centre, 2 pour droite.
 * @param {number} alignementVertical  0 pour alignement vers le haut, 1 pour centré, 2 pour bas.
 * @param {string} intitule  Le titre de la macro
 * @param {string} commentaireMacro  Eventuel commentaire explicatif.
 * @param {CPointLie} pointLieAPositionner
 * @param {CPointLie} pointLieAssocie
 * @param {boolean} fixed true si l'affichage est punaisé et ne peut pas être capturé à la souris
 * @returns {CMacroAffectationPointLie}
 */
function CMacroAffectationPointLie (listeProprietaire, impProto, estElementFinal, couleur,
  xNom, yNom, decX, decY, masque, pointLie, taillePolice, effacementFond, couleurFond,
  alignementHorizontal, alignementVertical, intitule, commentaireMacro, pointLieAPositionner,
  pointLieAssocie, fixed = false) {
  if (arguments.length !== 0) {
    if (arguments.length === 1) CMacro.call(this, listeProprietaire)
    else {
      CMacro.call(this, listeProprietaire, impProto, estElementFinal, couleur,
        xNom, yNom, decX, decY, masque, pointLie, taillePolice, effacementFond, couleurFond,
        alignementHorizontal, alignementVertical, intitule, commentaireMacro, fixed)
      this.pointLieAPositionner = pointLieAPositionner
      this.pointLieAssocie = pointLieAssocie
    }
  }
}
CMacroAffectationPointLie.prototype = new CMacro()
CMacroAffectationPointLie.prototype.constructor = CMacroAffectationPointLie
CMacroAffectationPointLie.prototype.superClass = 'CMacro'
CMacroAffectationPointLie.prototype.className = 'CMacroAffectationPointLie'

CMacroAffectationPointLie.prototype.getClone = function (listeSource, listeCible) {
  const ind1 = listeSource.indexOf(this.pointLieAPositionner)
  const ind2 = listeSource.indexOf(this.pointLieAssocie)
  const ind3 = listeSource.indexOf(this.pointLie)
  const ind4 = listeSource.indexOf(this.impProto)
  return new CMacroAffectationPointLie(listeCible, listeCible.get(ind4, 'CImplementationProto'),
    this.estElementFinal, this.couleur, this.xNom, this.yNom, this.decX, this.decY, this.masque,
    listeCible.get(ind3, 'CPt'), this.taillePolice, this.effacementFond, this.couleurFond,
    this.alignementHorizontal, this.alignementVertical,
    this.intitule, this.commentaireMacro, listeCible.get(ind1, 'CPointLie'),
    listeCible.get(ind2, 'CPointLie'), this.fixed)
}
CMacroAffectationPointLie.prototype.depDe = function (p) {
  if (this.elementTestePourDependDe === p) return this.dependDeElementTeste
  return this.memDep(CMacro.prototype.depDe.call(this, p) ||
    this.pointLieAPositionner.depDe(p) || this.pointLieAssocie.depDe(p))
}
CMacroAffectationPointLie.prototype.dependDePourBoucle = function (p) {
  return (CMacro.prototype.dependDePourBoucle.call(this, p) || this.pointLieAPositionner.dependDePourBoucle(p) ||
    this.pointLieAssocie.dependDePourBoucle(p))
}
CMacroAffectationPointLie.prototype.executionPossible = function () {
  return this.pointLieAPositionner.existe && this.pointLieAssocie.existe
}
CMacroAffectationPointLie.prototype.execute = function (svg, dimf, couleurFond) {
  this.pointLieAPositionner.donneAbscisse(this.pointLieAssocie.abscisse)
  this.listeProprietaire.positionne(false, dimf)
  this.listeProprietaire.update(svg, couleurFond, true)
  this.termineAction(svg, dimf, couleurFond)
}
CMacroAffectationPointLie.prototype.remplacePoint = function (ancienPoint, nouveauPoint) {
  if (this.pointLieAssocie === ancienPoint) this.pointLieAssocie = nouveauPoint
  if (this.pointLieAPositionner === ancienPoint) this.pointLieAPositionner = nouveauPoint
}
CMacroAffectationPointLie.prototype.ajouteAntecedents = function (liste) {
  liste.add(this.pointLieAPositionner)
  liste.add(this.pointLieAssocie)
}
CMacroAffectationPointLie.prototype.read = function (inps, list) {
  CMacro.prototype.read.call(this, inps, list)
  const ind1 = inps.readInt()
  this.pointLieAPositionner = list.get(ind1, 'CPointLie')
  const ind2 = inps.readInt()
  this.pointLieAssocie = list.get(ind2, 'CPointLie')
}
CMacroAffectationPointLie.prototype.write = function (oups, list) {
  CMacro.prototype.write.call(this, oups, list)
  const ind1 = list.indexOf(this.pointLieAPositionner)
  oups.writeInt(ind1)
  const ind2 = list.indexOf(this.pointLieAssocie)
  oups.writeInt(ind2)
}
